import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { api, getToken, setToken, BUSINESS } from "@/lib/api";
import { ArrowLeft, ImagePlus, Loader2, LogOut, Trash2, Mail, Phone, CalendarPlus, Check, X, Ban, Clock, Calendar as CalendarIcon, MapPin } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  useEffect(() => {
    if (!getToken()) return;
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => {
      setToken(null);
      setAuthed(false);
    }).finally(() => setChecking(false));
  }, []);

  if (checking) return <FullScreen><Loader2 className="h-6 w-6 animate-spin text-[#0B3A5B]" /></FullScreen>;

  return (
    <div className="min-h-screen bg-[#F3F5F7]">
      <header className="bg-white border-b border-[#0B3A5B]/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-[#0B3A5B]/80 hover:text-[#0B3A5B]">
            <ArrowLeft className="h-4 w-4" /> Retour au site
          </Link>
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="Hingant Clean" className="h-12 w-auto object-contain" />
            <span className="font-display text-[#0B3A5B] hidden sm:inline">Espace admin</span>
          </div>
          {authed && (
            <Button
              variant="ghost"
              data-testid="admin-logout"
              onClick={() => { setToken(null); setAuthed(false); }}
              className="text-[#2F3438]/70"
            >
              <LogOut className="h-4 w-4 mr-1" /> Déconnexion
            </Button>
          )}
          {!authed && <div className="w-20" />}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {authed ? <AdminDashboard /> : <LoginForm onSuccess={() => setAuthed(true)} />}
      </main>
    </div>
  );
}

function FullScreen({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-[#F3F5F7]">{children}</div>;
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("hingant.clean@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { email, password });
      setToken(res.data.token);
      toast.success("Connecté");
      onSuccess();
    } catch (err) {
      toast.error("Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-[0_20px_50px_-30px_rgba(11,58,91,0.35)] p-8 border border-[#0B3A5B]/8">
      <h1 className="font-display text-2xl text-[#0B3A5B]">Connexion administrateur</h1>
      <p className="mt-1 text-sm text-[#2F3438]/70">Gérez la galerie photo et consultez les demandes.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#2F3438]/70 font-semibold">Email</Label>
          <Input id="email" data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#2F3438]/70 font-semibold">Mot de passe</Label>
          <Input id="password" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
        </div>
        <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full h-12 rounded-full bg-[#0B3A5B] hover:bg-[#0a3251] text-white font-semibold">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Se connecter
        </Button>
      </form>
    </div>
  );
}

function AdminDashboard() {
  return (
    <Tabs defaultValue="gallery" className="space-y-6">
      <TabsList data-testid="admin-tabs" className="bg-white rounded-full p-1 h-12 border border-[#0B3A5B]/8 flex-wrap">
        <TabsTrigger data-testid="tab-gallery" value="gallery" className="rounded-full px-6 data-[state=active]:bg-[#0B3A5B] data-[state=active]:text-white">Galerie</TabsTrigger>
        <TabsTrigger data-testid="tab-slots" value="slots" className="rounded-full px-6 data-[state=active]:bg-[#0B3A5B] data-[state=active]:text-white">Disponibilités</TabsTrigger>
        <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-full px-6 data-[state=active]:bg-[#0B3A5B] data-[state=active]:text-white">Rendez-vous</TabsTrigger>
      </TabsList>
      <TabsContent value="gallery"><GalleryAdmin /></TabsContent>
      <TabsContent value="slots"><SlotsAdmin /></TabsContent>
      <TabsContent value="bookings"><BookingsList /></TabsContent>
    </Tabs>
  );
}

function GalleryAdmin() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/gallery");
      setImages(r.data || []);
    } catch (e) {
      toast.error("Impossible de charger la galerie");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/gallery");
        if (!cancelled) setImages(r.data || []);
      } catch {
        if (!cancelled) toast.error("Impossible de charger la galerie");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} dépasse 5 Mo — ignoré`);
          continue;
        }
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await api.post("/gallery", { title: title || file.name, image_data: dataUrl });
      }
      toast.success("Photo(s) ajoutée(s)");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Photo supprimée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-[#0B3A5B]/8 shadow-sm">
        <h2 className="font-display text-lg text-[#0B3A5B]">Ajouter des photos</h2>
        <p className="text-sm text-[#2F3438]/70 mt-1">JPEG, PNG ou WebP, jusqu&apos;à 5 Mo chacune.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            data-testid="gallery-title-input"
            placeholder="Titre (optionnel)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="sm:col-span-2"
          />
          <Button
            data-testid="gallery-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="h-10 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
            Ajouter des photos
          </Button>
        </div>
        <input
          ref={fileRef}
          data-testid="gallery-file-input"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onFile}
        />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-[#0B3A5B]/8 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg text-[#0B3A5B]">Photos publiées</h2>
          <span className="text-sm text-[#2F3438]/60" data-testid="gallery-count">{images.length} photo(s)</span>
        </div>
        {loading ? (
          <div className="mt-4 text-sm text-[#2F3438]/60">Chargement…</div>
        ) : images.length === 0 ? (
          <div className="mt-6 text-center py-12 border border-dashed border-[#0B3A5B]/15 rounded-xl text-sm text-[#2F3438]/60">
            Aucune photo personnalisée. Le site affiche les images par défaut.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((img) => (
              <div key={img.id} data-testid={`admin-image-${img.id}`} className="relative group rounded-xl overflow-hidden border border-[#0B3A5B]/8">
                <img src={img.image_data} alt={img.title} className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-[#0B3A5B]/0 group-hover:bg-[#0B3A5B]/60 transition flex items-center justify-center">
                  <Button
                    variant="destructive"
                    size="sm"
                    data-testid={`admin-image-delete-${img.id}`}
                    onClick={() => remove(img.id)}
                    className="opacity-0 group-hover:opacity-100 transition rounded-full"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
                  </Button>
                </div>
                {img.title && (
                  <div className="absolute bottom-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-t from-black/70 to-transparent truncate">
                    {img.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SlotsAdmin() {
  const [mode, setMode] = useState("single"); // 'single' | 'range'
  const [date, setDate] = useState(null);
  const [times, setTimes] = useState("09:00, 11:30, 14:00, 16:30");
  const [duration, setDuration] = useState(120);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5]); // Mon-Sat default
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/slots");
      setSlots(r.data || []);
    } catch { setSlots([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/slots");
        if (!cancelled) setSlots(r.data || []);
      } catch { if (!cancelled) setSlots([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const ymd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  const addSlots = async () => {
    if (!date) return toast.error("Choisissez une date.");
    const parsed = times.split(",").map((t) => t.trim()).filter(Boolean);
    if (!parsed.length) return toast.error("Indiquez au moins un horaire.");
    setCreating(true);
    try {
      await api.post("/admin/slots/bulk", {
        date: ymd(date),
        times: parsed,
        duration_minutes: Number(duration) || 120,
      });
      toast.success("Créneaux ajoutés");
      load();
    } catch (e) {
      toast.error("Erreur lors de la création");
    } finally { setCreating(false); }
  };

  const addRangeSlots = async () => {
    if (!rangeStart || !rangeEnd) return toast.error("Indiquez la plage de dates.");
    if (!weekdays.length) return toast.error("Sélectionnez au moins un jour de la semaine.");
    const parsed = times.split(",").map((t) => t.trim()).filter(Boolean);
    if (!parsed.length) return toast.error("Indiquez au moins un horaire.");
    setCreating(true);
    try {
      const r = await api.post("/admin/slots/range", {
        start_date: rangeStart,
        end_date: rangeEnd,
        weekdays,
        times: parsed,
        duration_minutes: Number(duration) || 120,
      });
      toast.success(`${r.data.created} créneau(x) créé(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  };

  const toggleWeekday = (d) => {
    setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));
  };

  const removeSlot = async (id) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try {
      await api.delete(`/admin/slots/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur";
      toast.error(msg);
    }
  };

  const toggleBlock = async (id) => {
    try {
      await api.post(`/admin/slots/${id}/toggle-block`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const blockPeriod = async () => {
    if (!blockStart || !blockEnd) return toast.error("Indiquez les deux dates.");
    try {
      const r = await api.post("/admin/slots/block-period", {
        start_date: blockStart, end_date: blockEnd, reason: "Congés",
      });
      toast.success(`${r.data.blocked} créneau(x) bloqué(s)`);
      load();
    } catch { toast.error("Erreur"); }
  };

  // Group by date
  const grouped = {};
  slots.forEach((s) => {
    const k = s.start.slice(0, 10);
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(s);
  });
  const dates = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-[#0B3A5B]/8 shadow-sm">
        <h2 className="font-display text-lg text-[#0B3A5B] flex items-center gap-2">
          <CalendarPlus className="h-5 w-5 text-[#1E88C8]" />
          Ajouter des créneaux
        </h2>

        {/* Mode switcher */}
        <div className="mt-4 inline-flex p-1 rounded-full bg-[#F3F5F7] border border-[#0B3A5B]/10" role="tablist">
          <button
            type="button"
            data-testid="mode-single"
            onClick={() => setMode("single")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
              mode === "single" ? "bg-[#0B3A5B] text-white" : "text-[#0B3A5B]"
            }`}
          >
            Jour précis
          </button>
          <button
            type="button"
            data-testid="mode-range"
            onClick={() => setMode("range")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
              mode === "range" ? "bg-[#0B3A5B] text-white" : "text-[#0B3A5B]"
            }`}
          >
            Plusieurs jours
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex justify-center">
            {mode === "single" ? (
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                data-testid="slots-calendar"
                disabled={(d) => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }}
                className="rounded-2xl border border-[#0B3A5B]/10"
              />
            ) : (
              <div data-testid="range-panel" className="w-full space-y-3">
                <Field label="Date de début">
                  <Input data-testid="range-start" type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                </Field>
                <Field label="Date de fin">
                  <Input data-testid="range-end" type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                </Field>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#2F3438]/70 font-semibold">
                    Jours de la semaine
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`weekday-${i}`}
                        onClick={() => toggleWeekday(i)}
                        className={`h-10 w-10 rounded-full text-sm font-bold border transition ${
                          weekdays.includes(i)
                            ? "bg-[#1E88C8] border-[#1E88C8] text-white"
                            : "bg-white border-[#0B3A5B]/15 text-[#0B3A5B]"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <Field label="Heures de début (séparées par , )">
              <Input data-testid="slots-times" value={times} onChange={(e) => setTimes(e.target.value)} placeholder="09:00, 11:30, 14:00" />
            </Field>
            <Field label="Durée d'un créneau (min)">
              <Input data-testid="slots-duration" type="number" min={30} step={15} value={duration} onChange={(e) => setDuration(e.target.value)} />
            </Field>
            <Button
              data-testid="slots-add"
              onClick={mode === "single" ? addSlots : addRangeSlots}
              disabled={creating}
              className="w-full h-11 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-2" />}
              {mode === "single" ? "Ajouter les créneaux" : "Générer la plage"}
            </Button>
            <div className="pt-3 border-t border-[#0B3A5B]/8">
              <p className="text-xs uppercase tracking-wider text-[#2F3438]/60 font-semibold mb-2">Période d&apos;indisponibilité (congés)</p>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="block-start" type="date" value={blockStart} onChange={(e) => setBlockStart(e.target.value)} />
                <Input data-testid="block-end" type="date" value={blockEnd} onChange={(e) => setBlockEnd(e.target.value)} />
              </div>
              <Button
                data-testid="block-period"
                onClick={blockPeriod}
                variant="outline"
                className="mt-2 w-full h-10 rounded-full"
              >
                <Ban className="h-4 w-4 mr-2" /> Bloquer la période
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-[#0B3A5B]/8 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-lg text-[#0B3A5B]">Créneaux existants</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#2F3438]/60" data-testid="slots-count">{slots.length} créneau(x)</span>
            <Button
              type="button"
              data-testid="delete-all-slots"
              variant="outline"
              size="sm"
              disabled={slots.length === 0}
              onClick={async () => {
                if (!window.confirm("Supprimer TOUS les créneaux non réservés ? Cette action est définitive.")) return;
                try {
                  const r = await api.delete("/admin/slots");
                  toast.success(`${r.data.deleted} créneau(x) supprimé(s)`);
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur");
                }
              }}
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Tout supprimer
            </Button>
          </div>
        </div>
        {loading ? <div className="mt-4 text-sm text-[#2F3438]/60">Chargement…</div> : dates.length === 0 ? (
          <div className="mt-6 text-center py-12 border border-dashed border-[#0B3A5B]/15 rounded-xl text-sm text-[#2F3438]/60">
            Aucun créneau. Ajoutez-en ci-dessus pour ouvrir votre calendrier aux clients.
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {dates.map((d) => (
              <div key={d}>
                <div className="text-sm font-semibold text-[#0B3A5B] mb-2">
                  {new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {grouped[d].map((s) => (
                    <div
                      key={s.id}
                      data-testid={`admin-slot-${s.id}`}
                      className={`rounded-xl border p-3 text-sm flex flex-col gap-2 ${
                        s.blocked ? "bg-amber-50 border-amber-200" :
                        s.booked ? "bg-emerald-50 border-emerald-200" :
                        "bg-white border-[#0B3A5B]/10"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold text-[#0B3A5B]">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-[11px] text-[#2F3438]/60">
                        {s.blocked ? "Bloqué" : s.booked ? "Réservé" : "Disponible"}
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          data-testid={`slot-toggle-${s.id}`}
                          onClick={() => toggleBlock(s.id)}
                          className="text-xs px-2 py-1 rounded-full bg-white border border-[#0B3A5B]/15 hover:bg-[#F3F5F7]"
                        >
                          {s.blocked ? "Débloquer" : "Bloquer"}
                        </button>
                        <button
                          type="button"
                          data-testid={`slot-delete-${s.id}`}
                          onClick={() => removeSlot(s.id)}
                          className="text-xs px-2 py-1 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50"
                          disabled={s.booked}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings");
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/bookings");
        if (!cancelled) setItems(r.data || []);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const act = async (id, action) => {
    if (!window.confirm(action === "validate" ? "Valider ce rendez-vous ?" : "Refuser ce rendez-vous ?")) return;
    try {
      await api.post(`/admin/bookings/${id}/${action}`, { note: "" });
      toast.success(action === "validate" ? "Rendez-vous validé" : "Demande refusée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce rendez-vous ? Le créneau sera libéré.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Rendez-vous supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = filter === "all" ? items : items.filter((b) => b.status === filter);

  if (loading) return <Card>Chargement…</Card>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: "Toutes" },
          { k: "pending", label: "En attente" },
          { k: "validated", label: "Validés" },
          { k: "refused", label: "Refusés" },
          { k: "expired", label: "Expirés" },
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            data-testid={`filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${
              filter === f.k ? "bg-[#0B3A5B] text-white" : "bg-white text-[#0B3A5B] border border-[#0B3A5B]/15"
            }`}
          >
            {f.label} ({f.k === "all" ? items.length : items.filter((b) => b.status === f.k).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card>Aucune demande {filter !== "all" ? `(${filter})` : ""}.</Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => {
            const slotDate = b.slot_start
              ? new Date(b.slot_start).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
              : "—";
            return (
              <Card key={b.id} testid={`booking-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-[#0B3A5B] text-lg">{b.name}</div>
                    <div className="text-xs text-[#2F3438]/60 mt-0.5">
                      Demande du {new Date(b.created_at).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <Line icon={<CalendarIcon className="h-3.5 w-3.5" />} value={slotDate} />
                  <Line label="Formule" value={b.formule} />
                  <Line icon={<Phone className="h-3.5 w-3.5" />} value={b.phone} />
                  {b.email && <Line icon={<Mail className="h-3.5 w-3.5" />} value={b.email} />}
                  {b.vehicle && <Line label="Véhicule" value={b.vehicle} />}
                  {b.address && <Line icon={<MapPin className="h-3.5 w-3.5" />} value={b.address} />}
                  {Array.isArray(b.options) && b.options.length > 0 && (
                    <Line label="Options" value={b.options.join(", ")} />
                  )}
                  {b.total_price != null && (
                    <Line label="Total" value={`${b.total_price},00 €`} />
                  )}
                </div>
                {b.message && <p className="mt-3 text-sm text-[#2F3438]/85 whitespace-pre-wrap bg-[#F3F5F7] p-3 rounded-lg">{b.message}</p>}
                {b.status === "pending" && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button
                      data-testid={`validate-${b.id}`}
                      onClick={() => act(b.id, "validate")}
                      className="flex-1 min-w-[120px] h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Check className="h-4 w-4 mr-1" /> Valider
                    </Button>
                    <Button
                      data-testid={`refuse-${b.id}`}
                      onClick={() => act(b.id, "refuse")}
                      variant="outline"
                      className="flex-1 min-w-[120px] h-10 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 mr-1" /> Refuser
                    </Button>
                    <Button
                      data-testid={`delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      variant="ghost"
                      className="h-10 rounded-full text-[#2F3438]/60 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                )}
                {b.status !== "pending" && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      data-testid={`delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-[#2F3438]/60 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[#2F3438]/70 font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    pending: { c: "bg-amber-50 text-amber-700 border-amber-200", label: "En attente" },
    validated: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Validé" },
    refused: { c: "bg-red-50 text-red-700 border-red-200", label: "Refusé" },
    expired: { c: "bg-slate-100 text-slate-600 border-slate-200", label: "Expiré" },
  }[status] || { c: "bg-slate-100 text-slate-600", label: status };
  return (
    <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${cfg.c}`}>
      {cfg.label}
    </span>
  );
}

function Card({ children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl p-5 border border-[#0B3A5B]/8 shadow-sm">{children}</div>
  );
}
function Line({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-[#2F3438]/85">
      {icon ? icon : <span className="text-xs uppercase tracking-wider text-[#2F3438]/50 w-20">{label}</span>}
      <span>{value}</span>
    </div>
  );
}
